import { useState } from 'react';
import type { CSSProperties } from 'react';
import GradientBackground from '../components/GradientBackground';
import GlassCard from '../components/GlassCard';

export type LandingPageProps = {
  /** Called when user clicks the primary CTA */
  onLogin: () => Promise<void> | void;
  /** Optional generic auth error to display (e.g., not authorized) */
  authError?: { kind: 'NOT_AUTHORIZED' | 'UNKNOWN'; message: string } | null;
};

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2L3 6.5v5C3 16.7 6.9 21.5 12 23c5.1-1.5 9-6.3 9-11.5v-5L12 2z"
      stroke="url(#shield-grad)"
      strokeWidth="1.8"
      strokeLinejoin="round"
      fill="url(#shield-fill)"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient id="shield-grad" x1="3" y1="2" x2="21" y2="23" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#c084fc" />
        <stop offset="100%" stopColor="#60a5fa" />
      </linearGradient>
      <linearGradient id="shield-fill" x1="3" y1="2" x2="21" y2="23" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="rgba(168,85,247,0.15)" />
        <stop offset="100%" stopColor="rgba(59,130,246,0.15)" />
      </linearGradient>
    </defs>
  </svg>
);

const LogoRing = () => (
  <div style={logoRingContainerStyle}>
    <div style={outerRingStyle} />
    <div style={innerRingStyle} />
    <div style={iconContainerStyle}>
      <ShieldIcon />
    </div>
  </div>
);

const ServiceChip = ({ icon, label, style }: { icon: string; label: string; style?: CSSProperties }) => (
  <div style={{ ...chipStyle, ...style }}>
    <span style={{ fontSize: '14px' }}>{icon}</span>
    <span style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.02em' }}>
      {label}
    </span>
  </div>
);

const DEFAULT_NOT_AUTHORIZED_MESSAGE =
  'Only @hopper.dev Google accounts are permitted. Please use a Hopper email to sign in.';
const LOGIN_START_FAILED_MESSAGE = 'Something went wrong starting sign-in. Please try again.';

export default function LandingPage({ onLogin, authError }: LandingPageProps) {
  const [hoverCta, setHoverCta] = useState(false);
  const [hoverGoogle, setHoverGoogle] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const showError = (authError && !dismissed) || Boolean(localError);
  // authError.message drives the displayed copy; NOT_AUTHORIZED keeps a safe
  // generic default so backend error detail is never leaked to the user.
  const errorMessage = localError
    ? localError
    : authError?.kind === 'NOT_AUTHORIZED'
      ? DEFAULT_NOT_AUTHORIZED_MESSAGE
      : authError?.message ?? DEFAULT_NOT_AUTHORIZED_MESSAGE;

  const handleLogin = () => {
    setLocalError(null);
    Promise.resolve()
      .then(() => onLogin())
      .catch(() => {
        setDismissed(false);
        setLocalError(LOGIN_START_FAILED_MESSAGE);
      });
  };

  return (
    <GradientBackground>
      <div style={layoutStyle}>
        {/* ---- Top-edge label ---- */}
        <div className="anim-fade-in" style={topLabelStyle}>
          <div style={topDotStyle} />
          <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.06em', fontWeight: 500 }}>
            social-login.kai-sandbox.hopper.dev
          </span>
        </div>

        {/* ---- Floating service chips (decorative) ---- */}
        <ServiceChip icon="☁️" label="AWS Cognito" style={{ top: '18%', left: '6%', animationDelay: '0s' }} />
        <ServiceChip icon="🔐" label="JWT Auth" style={{ top: '28%', left: '3%', animationDelay: '0.3s' }} />
        <ServiceChip icon="⚡" label="Serverless" style={{ bottom: '28%', left: '5%', animationDelay: '0.6s' }} />
        <ServiceChip icon="🌐" label="Google IdP" style={{ top: '20%', right: '5%', animationDelay: '0.2s' }} />
        <ServiceChip icon="🛡️" label="PKCE Flow" style={{ top: '33%', right: '3%', animationDelay: '0.5s' }} />
        <ServiceChip icon="📋" label="ID Claims" style={{ bottom: '30%', right: '5%', animationDelay: '0.8s' }} />

        {/* ======================================================= */}
        {/*  HERO GLASS CARD                                         */}
        {/* ======================================================= */}
        <GlassCard elevated style={cardStyle}>
          {/* ---- Auth-error banner ---- */}
          {showError && (
            <div style={errorBannerStyle}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '1px' }}>⚠️</span>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 700, color: '#fca5a5', marginBottom: '2px' }}>
                    Not authorized
                  </p>
                  <p style={{ fontSize: '12px', color: 'rgba(252,165,165,0.75)', lineHeight: 1.5 }}>
                    {errorMessage}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setDismissed(true);
                    setLocalError(null);
                  }}
                  style={errorDismissStyle}
                  aria-label="Dismiss"
                >
                  ✕
                </button>
              </div>
              <button
                onClick={handleLogin}
                style={retryBtnStyle}
              >
                Try a different account →
              </button>
            </div>
          )}

          {/* ---- Logo ---- */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '32px' }}>
            <LogoRing />
          </div>

          {/* ---- App name label ---- */}
          <p className="anim-fade-in-up delay-100" style={appLabelStyle}>
            Social Login Demo
          </p>

          {/* ---- Coming Soon headline ---- */}
          <h1 className="anim-fade-in-up delay-200" style={headlineStyle}>
            Coming&nbsp;
            <span style={gradientTextStyle}>Soon</span>
          </h1>

          {/* ---- Supporting copy ---- */}
          <p className="anim-fade-in-up delay-300" style={subtextStyle}>
            A living proof-of-concept for Cognito Hosted UI + Google Sign-In.
            <br />
            End-to-end auth with a protected API — for Hopper engineers.
          </p>

          {/* ---- Feature pills ---- */}
          <div className="anim-fade-in-up delay-400" style={pillRowStyle}>
            {['Google SSO', 'JWT Protected API', '@hopper.dev only', 'Serverless'].map((label) => (
              <span key={label} style={pillStyle}>
                {label}
              </span>
            ))}
          </div>

          {/* ---- Divider ---- */}
          <div className="anim-fade-in-up delay-400" style={dividerStyle} />

          {/* ---- Primary CTA: Sign up now ---- */}
          <button
            className="anim-fade-in-up delay-500"
            type="button"
            style={{
              ...ctaButtonStyle,
              ...(hoverCta ? ctaButtonHoverStyle : {}),
            }}
            onMouseEnter={() => setHoverCta(true)}
            onMouseLeave={() => setHoverCta(false)}
            onClick={handleLogin}
          >
            <span style={{ position: 'relative', zIndex: 1 }}>Sign up now</span>
            <span style={{ position: 'relative', zIndex: 1, fontSize: '18px', marginTop: '1px' }}>→</span>
            <div style={ctaShimmerStyle} />
          </button>

          {/* ---- OR separator ---- */}
          <div className="anim-fade-in-up delay-500" style={orSeparatorStyle}>
            <div style={orLineStyle} />
            <span style={orTextStyle}>or</span>
            <div style={orLineStyle} />
          </div>

          {/* ---- Google button ---- */}
          <button
            className="anim-fade-in-up delay-600"
            style={{
              ...googleBtnStyle,
              ...(hoverGoogle ? googleBtnHoverStyle : {}),
            }}
            onMouseEnter={() => setHoverGoogle(true)}
            onMouseLeave={() => setHoverGoogle(false)}
            onClick={handleLogin}
          >
            <GoogleIcon />
            <span style={{ fontSize: '14px', fontWeight: 600, color: 'rgba(255,255,255,0.88)' }}>
              Continue with Google
            </span>
          </button>

          {/* ---- Footer note ---- */}
          <p className="anim-fade-in-up delay-600" style={footerNoteStyle}>
            Access restricted to <code style={codeStyle}>@hopper.dev</code> accounts only
          </p>
        </GlassCard>

        {/* ---- Bottom powered-by footer ---- */}
        <div className="anim-fade-in delay-600" style={poweredByStyle}>
          <span>Powered by</span>
          <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>AWS Cognito</span>
          <span style={dotDividerStyle}>·</span>
          <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>API Gateway</span>
          <span style={dotDividerStyle}>·</span>
          <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>Lambda</span>
          <span style={dotDividerStyle}>·</span>
          <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>CDK</span>
        </div>
      </div>
    </GradientBackground>
  );
}

/* ================================================================== */
/*  Styles                                                              */
/* ================================================================== */

const layoutStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '80px 24px 40px',
  position: 'relative',
};

const topLabelStyle: CSSProperties = {
  position: 'fixed',
  top: '18px',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '20px',
  padding: '5px 14px',
  zIndex: 50,
};

const topDotStyle: CSSProperties = {
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  background: '#4ade80',
  boxShadow: '0 0 6px #4ade80',
  animation: 'pulse-glow 2.5s ease-in-out infinite',
};

const chipStyle: CSSProperties = {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.09)',
  borderRadius: '12px',
  padding: '6px 12px',
  animation: 'float-orb-3 8s ease-in-out infinite',
};

const cardStyle: CSSProperties = {
  width: '100%',
  maxWidth: '480px',
  textAlign: 'center',
};

const errorBannerStyle: CSSProperties = {
  background: 'rgba(239,68,68,0.1)',
  border: '1px solid rgba(239,68,68,0.3)',
  borderRadius: '14px',
  padding: '14px 16px',
  marginBottom: '28px',
  textAlign: 'left',
  animation: 'slide-in-down 0.3s cubic-bezier(0.16,1,0.3,1) both',
};

const errorDismissStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(252,165,165,0.6)',
  cursor: 'pointer',
  fontSize: '14px',
  padding: '0 0 0 8px',
  marginLeft: 'auto',
  flexShrink: 0,
};

const retryBtnStyle: CSSProperties = {
  background: 'rgba(239,68,68,0.15)',
  border: '1px solid rgba(239,68,68,0.3)',
  borderRadius: '8px',
  padding: '8px 14px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#fca5a5',
  cursor: 'pointer',
  marginTop: '12px',
  width: '100%',
};

const logoRingContainerStyle: CSSProperties = {
  position: 'relative',
  width: '88px',
  height: '88px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const outerRingStyle: CSSProperties = {
  position: 'absolute',
  inset: '-4px',
  borderRadius: '50%',
  background: 'transparent',
  border: '1px solid rgba(168,85,247,0.2)',
  animation: 'pulse-glow 3s ease-in-out infinite',
};

const innerRingStyle: CSSProperties = {
  position: 'absolute',
  inset: '-12px',
  borderRadius: '50%',
  background: 'transparent',
  border: '1px solid rgba(168,85,247,0.08)',
  animation: 'pulse-glow 3s ease-in-out infinite',
  animationDelay: '1.5s',
};

const iconContainerStyle: CSSProperties = {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background:
    'radial-gradient(circle at 35% 35%, rgba(168,85,247,0.3), rgba(59,130,246,0.15)), rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.12)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow:
    '0 0 0 1px rgba(255,255,255,0.04), 0 8px 24px rgba(0,0,0,0.4), 0 0 40px rgba(168,85,247,0.2)',
};

const appLabelStyle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'rgba(168,85,247,0.85)',
  marginBottom: '12px',
};

const headlineStyle: CSSProperties = {
  fontSize: 'clamp(44px, 8vw, 64px)',
  fontWeight: 800,
  lineHeight: 1.05,
  letterSpacing: '-0.03em',
  color: '#ffffff',
  marginBottom: '18px',
};

const gradientTextStyle: CSSProperties = {
  background: 'linear-gradient(135deg, #e879f9 0%, #c084fc 35%, #818cf8 65%, #60a5fa 100%)',
  backgroundSize: '200% auto',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  animation: 'shimmer-text 4s linear infinite',
};

const subtextStyle: CSSProperties = {
  fontSize: '15px',
  lineHeight: 1.7,
  color: 'rgba(255,255,255,0.55)',
  marginBottom: '24px',
  fontWeight: 400,
};

const pillRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  justifyContent: 'center',
  marginBottom: '28px',
};

const pillStyle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.04em',
  color: 'rgba(255,255,255,0.5)',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '20px',
  padding: '4px 12px',
};

const dividerStyle: CSSProperties = {
  height: '1px',
  background:
    'linear-gradient(90deg, transparent, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.1) 70%, transparent)',
  marginBottom: '28px',
};

const ctaButtonStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  padding: '17px 32px',
  borderRadius: '14px',
  border: 'none',
  textDecoration: 'none',
  background: 'linear-gradient(135deg, #a855f7 0%, #7c3aed 40%, #4f46e5 70%, #3b82f6 100%)',
  backgroundSize: '200% 100%',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 700,
  letterSpacing: '-0.01em',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  boxShadow:
    '0 4px 20px rgba(139,92,246,0.4), 0 0 0 1px rgba(255,255,255,0.1) inset',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  marginBottom: '16px',
};

const ctaButtonHoverStyle: CSSProperties = {
  transform: 'translateY(-2px) scale(1.01)',
  boxShadow:
    '0 8px 32px rgba(139,92,246,0.55), 0 0 0 1px rgba(255,255,255,0.15) inset',
};

const ctaShimmerStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: '-100%',
  width: '60%',
  height: '100%',
  background:
    'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
  transform: 'skewX(-20deg)',
  animation: 'shimmer-text 2.5s ease-in-out infinite',
};

const orSeparatorStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '16px',
};

const orLineStyle: CSSProperties = {
  flex: 1,
  height: '1px',
  background: 'rgba(255,255,255,0.08)',
};

const orTextStyle: CSSProperties = {
  fontSize: '12px',
  color: 'rgba(255,255,255,0.3)',
  fontWeight: 500,
  flexShrink: 0,
};

const googleBtnStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '14px 24px',
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.14)',
  background: 'rgba(255,255,255,0.07)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  marginBottom: '20px',
};

const googleBtnHoverStyle: CSSProperties = {
  background: 'rgba(255,255,255,0.12)',
  border: '1px solid rgba(255,255,255,0.22)',
};

const footerNoteStyle: CSSProperties = {
  fontSize: '12px',
  color: 'rgba(255,255,255,0.3)',
  lineHeight: 1.6,
};

const codeStyle: CSSProperties = {
  fontFamily: 'monospace',
  background: 'rgba(168,85,247,0.15)',
  color: 'rgba(196,148,255,0.9)',
  padding: '1px 5px',
  borderRadius: '4px',
  fontSize: '11px',
};

const poweredByStyle: CSSProperties = {
  marginTop: '32px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '12px',
  color: 'rgba(255,255,255,0.28)',
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const dotDividerStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.2)',
};
